export const DECISION_INTERVENTION_PATH = '/decision-support-intervention';

export const STEPS = [
  { key: 'basicInformation', label: 'Basic Information' },
  { key: 'triggerConditions', label: 'Trigger Conditions' },
  { key: 'recommendation', label: 'Recommendation' },
  { key: 'metadata', label: 'Metadata' },
  { key: 'reviewSubmit', label: 'Review & Submit' },
] as const;

export type StepKey = (typeof STEPS)[number]['key'];

export const severityOptions = [
  { value: 'info', label: 'Info', color: '#1E88E5' },
  { value: 'warning', label: 'Warning', color: '#F9A825' },
  { value: 'critical', label: 'Critical', color: '#E53935' },
] as const;

export type SeverityLevel = (typeof severityOptions)[number]['value'];

export type TriggerType = 'woundType' | 'icd10';

export type TriggerConditionOption = {
  value: string;
  label: string;
  description?: string;
};

export type TriggerCondition = {
  id: string;
  type: TriggerType;
  code: string;
  label: string;
  description?: string;
};

export type WizardState = {
  basicInformation: {
    name: string;
    severity: '' | SeverityLevel;
  };
  triggerConditions: {
    selectedType: TriggerType;
    selectedValue: string;
    conditions: TriggerCondition[];
  };
  recommendation: {
    clinicalRecommendation: string;
  };
  metadata: {
    evidenceSource: string;
    validationDate: string;
    developer: string;
    version: string;
    fairnessNotes: string;
    performanceSummary: string;
    disclosureStatement: string;
  };
};

export const formatDateForInput = (value: Date) =>
  value.toISOString().slice(0, 10);

export const formatDateForDisplay = (value?: string) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString();
};

export const defaultState: WizardState = {
  basicInformation: {
    name: '',
    severity: '',
  },
  triggerConditions: {
    selectedType: 'woundType',
    selectedValue: '',
    conditions: [],
  },
  recommendation: {
    clinicalRecommendation: '',
  },
  metadata: {
    evidenceSource: '',
    validationDate: formatDateForInput(new Date()),
    developer: '',
    version: '',
    fairnessNotes: '',
    performanceSummary: '',
    disclosureStatement: '',
  },
};

export type DsiPlanDefinitionStatus = 'active' | 'inactive';

export type DsiSeverityKind = 'info' | 'warning' | 'critical';

export type DsiTriggerTypeKind =
  | 'CONDITION'
  | 'OBSERVATION'
  | 'MEDICATIONSTATEMENT'
  | 'ALLERGY'
  | 'IMMUNIZATION';

export type CodeInput = {
  system: string;
  code: string;
  display?: string;
};

export type DsiTriggerInput = {
  type: DsiTriggerTypeKind;
  code: CodeInput;
};

export type DecisionSupportMetadataInput = {
  evidenceTitle?: string;
  evidenceUrl?: string;
  validationDate?: string;
  developer?: string;
  version?: string;
  fairnessNotes?: string;
  performanceSummary?: string;
  disclosureStatement?: string;
};

export type DecisionSupportInput = {
  id?: string;
  title: string;
  status?: DsiPlanDefinitionStatus;
  severity: DsiSeverityKind;
  triggers?: DsiTriggerInput[];
  recommendation: string;
  metadata?: DecisionSupportMetadataInput;
};

export const DEFAULT_PLAN_DEFINITION_STATUS: DsiPlanDefinitionStatus = 'active';

export const TRIGGER_TYPE_TO_DSI_META: Record<
  TriggerType,
  { type: DsiTriggerTypeKind; system: string }
> = {
  woundType: {
    type: 'OBSERVATION',
    system: 'http://woundtech.net/fhir/CodeSystem/dsi-wound-type', // NOSONAR
  },
  icd10: {
    type: 'CONDITION',
    system: 'http://hl7.org/fhir/sid/icd-10-cm', // NOSONAR
  },
};

export const VALUE_SET_DSI_TYPE =
  'http://woundtech.net/fhir/CodeSystem/dsi-type'; // NOSONAR

export const PDSI_COMING_SOON_PATH = `${DECISION_INTERVENTION_PATH}/pdsi/coming-soon`;

export const INTERVENTION_TYPE_LABELS = {
  DSI: 'Evidence-Based DSI',
  PDSI: 'Predictive DSI (PDSI)',
} as const;

export const INTERVENTION_TYPE_VALUES = {
  DSI: 'dsi',
  PDSI: 'pdsi',
} as const;

export type InterventionTypeKind =
  (typeof INTERVENTION_TYPE_VALUES)[keyof typeof INTERVENTION_TYPE_VALUES];

export const INTERVENTION_TYPE_BADGE_LABELS: Record<
  InterventionTypeKind,
  string
> = {
  dsi: 'DSI',
  pdsi: 'PDSI',
};

export const interventionTypeFilterOptions = [
  { value: '', label: 'All Types' },
  { value: INTERVENTION_TYPE_VALUES.DSI, label: 'DSI' },
  { value: INTERVENTION_TYPE_VALUES.PDSI, label: 'PDSI' },
] as const;

export const statusFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

export const severityFilterOptions = [
  { value: '', label: 'All Severities' },
  ...severityOptions.map((option) => ({
    value: option.value,
    label: option.label,
  })),
] as const;

export type DecisionSupportListApiFilters = {
  search?: string;
  status?: DsiPlanDefinitionStatus;
};

export type InterventionListFilterValues = {
  search?: string;
  interventionType?: string;
  severity?: string;
  status?: string;
};

export const DASHBOARD_LIST_PAGE_PARAMS = {
  currentPage: 1,
  recordsPerPage: 50,
} as const;

export const DASHBOARD_SUMMARY_PAGE_PARAMS = {
  currentPage: 1,
  recordsPerPage: 1000,
} as const;

export const DASHBOARD_SORT_PARAMS = {
  sortBy: 'lastModifiedDate',
  sortOrder: 'desc',
} as const;
